import { createApi } from "@reduxjs/toolkit/query/react";
import { VITE_API_V2_URL } from "codes";
import { axiosBaseQuery } from "helpers/axiosbaseQuery";
import type { BankAccount } from "types";
import { accountrtk } from "./api-accounts";

export interface VirtualAccount {
  id: string;
  bank_name: string;
  swift_code?: string;
  account_number: string;
  beneficiary_name?: string;
  currency?: string;
  street_1?: string;
  street_2?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  is_active: boolean;
}

const formatBankAccount = (banks: unknown): any[] => {
  if (!Array.isArray(banks)) return [];
  return banks.map((bank: BankAccount) => {
    const rawBank = bank as BankAccount & Record<string, any>;
    const allData =
      rawBank.all_data && typeof rawBank.all_data === "object"
        ? rawBank.all_data
        : {};
    const ibanCode =
      rawBank.iban_code ??
      rawBank.iban ??
      rawBank.IBAN ??
      rawBank.bank_iban ??
      rawBank.iban_number ??
      rawBank.IBAN__c ??
      allData.iban_code ??
      allData.iban ??
      allData.IBAN;

    return {
      id: bank.id,
      bank_name: bank.bank_name,
      bank_account_number: bank.bank_account_number,
      beneficiary_name: bank.beneficiary_name,
      currency: bank.currency,
      country: bank.bank_address?.country,
      swift_code: bank.swift_code,
      iban_code: ibanCode,
      iban: ibanCode,
      sf_id: bank.sf_id,
      bank_address: bank.bank_address,
      name: bank.name,
    };
  });
};
const ACCOUNTS_V2 = "/accounts";
export const banks = createApi({
  reducerPath: "banks",
  tagTypes: ["BANK", "BANK_ACCOUNT_DOCUMENTS", "VIRTUAL_ACCOUNT"],
  baseQuery: axiosBaseQuery({
    baseUrl: `${VITE_API_V2_URL}${ACCOUNTS_V2}/`,
  }),
  endpoints: (builder) => ({
    getVirtualAccounts: builder.query<VirtualAccount[], string>({
      query: (accountId) => ({
        url: `${accountId}/virtual_account`,
        method: "GET",
      }),
      transformResponse: (response: any) => {
        const data = response?.data ?? response;
        return Array.isArray(data)
          ? data
          : Array.isArray(data?.virtual_accounts)
            ? data.virtual_accounts
            : [];
      },
      providesTags: (_result, _error, accountId) => [
        { type: "VIRTUAL_ACCOUNT", id: accountId },
      ],
    }),
    createVirtualAccount: builder.mutation<
      VirtualAccount,
      { accountId: string; data: Omit<VirtualAccount, "id"> }
    >({
      query: ({ accountId, data }) => ({
        url: `${accountId}/virtual_account`,
        method: "POST",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "VIRTUAL_ACCOUNT", id: accountId },
      ],
    }),
    updateVirtualAccount: builder.mutation<
      VirtualAccount,
      {
        accountId: string;
        virtualAccountId: string;
        data: Partial<Omit<VirtualAccount, "id">>;
      }
    >({
      query: ({ accountId, virtualAccountId, data }) => ({
        url: `${accountId}/virtual_account/${virtualAccountId}`,
        method: "PUT",
        data,
      }),
      transformResponse: (response: any) => response?.data ?? response,
      invalidatesTags: (_result, _error, { accountId }) => [
        { type: "VIRTUAL_ACCOUNT", id: accountId },
      ],
    }),
    getBankAccount: builder.query<
      BankAccount,
      { account_id: string; bankaccount?: string }
    >({
      query: ({
        account_id,
        bankaccount,
      }: {
        account_id: string;
        bankaccount?: string;
      }) => ({
        url: `${account_id}/bank-accounts/${bankaccount}`,
        method: "get",
      }),
      transformResponse: (response) => response.data,
      providesTags: (result) => [{ type: "BANK", id: result?.id }],
    }),
    getBankAccounts: builder.query<BankAccount[], string>({
      query: (account_id) => ({
        url: `${account_id}/bank-accounts`,
        method: "get",
      }),
      transformResponse: (response) => formatBankAccount(response?.data),
      providesTags: ["BANK"],
    }),
    updateBankAccount: builder.mutation<
      BankAccount,
      { bankAccountId: string; data: BankAccount; account_id: string }
    >({
      query(payload) {
        const { bankAccountId, data, account_id } = payload;
        return {
          url: `${account_id}/bank-accounts/${bankAccountId}`,
          method: "PUT",
          data,
        };
      },
      invalidatesTags: ["BANK"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(
            accountrtk.util.invalidateTags(["ACCOUNT_ONBOARDING_STATUS"])
          );
        } catch (err) {
          // Error handled silently
        }
      },
    }),
    registerBankAccount: builder.mutation<
      BankAccount,
      { account_id: string; data: BankAccount }
    >({
      query: ({ account_id, data }) => {
        return {
          url: `${account_id}/bank-accounts`,
          method: "POST",
          data,
        };
      },
      invalidatesTags: ["BANK"],
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          // Invalidate onboarding status to refresh the status
          // Note: Green tick only shows when documents are also uploaded
          dispatch(
            accountrtk.util.invalidateTags(["ACCOUNT_ONBOARDING_STATUS"])
          );
        } catch (err) {
          // Error handled silently
        }
      },
    }),
    deleteBankAccount: builder.mutation<
      any,
      { account_id: string; bank_account_id: string }
    >({
      query: ({ account_id, bank_account_id }) => {
        return {
          url: `${account_id}/bank-accounts/${bank_account_id}`,
          method: "DELETE",
        };
      },
      invalidatesTags: ["BANK"],
    }),
    uploadBankAccountDocument: builder.mutation<
      any,
      { account_id: string; file: File; bank_account_id?: string }
    >({
      query: ({ account_id, file, bank_account_id }) => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("document_type", "bank_account");
        formData.append("document_category", "bank_account");
        if (bank_account_id) {
          formData.append("bank_account_id", bank_account_id);
        }
        return {
          url: `${account_id}/documents`,
          method: "POST",
          data: formData,
        };
      },
      invalidatesTags: (result, error, arg) => [
        "BANK",
        { type: "BANK_ACCOUNT_DOCUMENTS", id: arg.bank_account_id },
        "BANK_ACCOUNT_DOCUMENTS",
      ],
      transformResponse: (response) => response?.data,
      async onQueryStarted(arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(accountrtk.util.invalidateTags(["ACCOUNT_DOCS"]));
          // Invalidate bank account documents query to force refetch
          dispatch(
            banks.util.invalidateTags([
              { type: "BANK_ACCOUNT_DOCUMENTS", id: arg.bank_account_id },
              "BANK_ACCOUNT_DOCUMENTS",
            ])
          );
        } catch (err) {
          // Error handled silently
        }
      },
    }),
    getBankAccountDocuments: builder.query<
      any[],
      { account_id: string; bank_account_id?: string }
    >({
      query: ({ account_id, bank_account_id }) => {
        // Use query parameters to filter by bank_account_id on the backend
        const params: Record<string, string> = {
          document_type: "bank_account",
        };
        if (bank_account_id) {
          params.bank_account_id = bank_account_id;
        }
        return {
          url: `${account_id}/documents`,
          method: "get",
          params,
        };
      },
      transformResponse: (response, meta, arg) => {
        const docs = Array.isArray(response?.data) ? response?.data : [response?.data].filter(Boolean);
        
        // Strict filtering: Only return bank_account documents
        const bankAccountDocs = docs.filter(
          (doc: any) =>
            doc.document_type === "bank_account" ||
            doc.document_category === "bank_account"
        );
        
        // STRICT FILTERING: If bank_account_id is provided, ONLY return documents that match it
        // No fallback - if documents don't have bank_account_id field, return empty array
        if (arg.bank_account_id) {
          const filteredDocs = bankAccountDocs.filter((doc: any) => {
            // Check all possible field names where bank_account_id might be stored
            const docBankAccountId = 
              doc.bank_account_id ||
              doc.bank_account?.id ||
              doc.bank_account_id?.toString();
            const filterBankAccountId = arg.bank_account_id?.toString();
            return docBankAccountId && docBankAccountId === filterBankAccountId;
          });
          // Return only matching documents - empty array if none match
          return filteredDocs;
        }
        
        // If no bank_account_id filter, return all bank_account documents
        return bankAccountDocs;
      },
      providesTags: (result, error, arg) => [
        { type: "BANK_ACCOUNT_DOCUMENTS", id: arg.bank_account_id },
        "BANK_ACCOUNT_DOCUMENTS",
      ],
    }),
    getBankAccountDocumentUrl: builder.query<
      any,
      { account_id: string; document_id: string }
    >({
      query: ({ account_id, document_id }) => ({
        url: `${account_id}/documents/${document_id}`,
        method: "get",
      }),
      transformResponse: (response) => response?.data,
    }),
  }),
});

export const {
  useGetVirtualAccountsQuery,
  useCreateVirtualAccountMutation,
  useUpdateVirtualAccountMutation,
  useGetBankAccountsQuery,
  useLazyGetBankAccountsQuery,
  useGetBankAccountQuery,
  useUpdateBankAccountMutation,
  useRegisterBankAccountMutation,
  useDeleteBankAccountMutation,
  useUploadBankAccountDocumentMutation,
  useGetBankAccountDocumentsQuery,
  useLazyGetBankAccountDocumentsQuery,
  useGetBankAccountDocumentUrlQuery,
  useLazyGetBankAccountDocumentUrlQuery,
} = banks;
